import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import { faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box } from '@mui/material';
import { styled } from '@mui/system';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

export const StyledPageWrapper = styled(Box)(() => {
  return {
    padding: '2rem',
    fontSize: '0.75rem',
    paddingTop: `4.625rem`,
    backgroundColor: `${tsoPqTheme.palette.background.other.color_other_background}`,
  };
});

export const StyledGridWrapper = styled(Box)(() => {
  return {
    height: '95%',
    width: '100%',
  };
});

export const SharedStyledDataGrid = styled(DataGridPro)(() => ({
  fontFamily: tsoPqTheme.typography.openSans,
  '& .customBackgroundHeader': {
    backgroundColor: tsoPqTheme.palette.table.color_other_table_header,
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-cell--withRenderer': {
    userSelect: 'none',
  },
  '& .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
    opacity: 1,
    visibility: 'visible',
    color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
    zIndex: '999',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    whiteSpace: 'pre-wrap',
    fontWeight: '600',
    fontSize: '0.875rem',
    fontFamily: tsoPqTheme.typography.openSans,
    lineHeight: '1rem',
    textAlign: 'left',
    letterSpacing: '0.007rem',
  },
  '& .MuiDataGrid-sortIcon': {
    visibility: 'visible',
    opacity: 1,
  },
  '& .MuiDataGrid-sortButton': {
    backgroundColor: 'transparent !important',
  },
}));

export const StyledDataGrid = styled(SharedStyledDataGrid)(() => ({
  backgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
  color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
  '& .even-row': {
    backgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
    maxHeight: '2.625rem',
  },
  '& .odd-row': {
    maxHeight: '2.625rem',
    backgroundColor: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_neutral_lt,
  },

  '& .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
    color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
    zIndex: '999',
  },

  '& .MuiDataGrid-cell[data-field="gapColumnLeft"]': {
    padding: 0,
  },

  '& .MuiDataGrid-cell[data-field="gapColumnRight"]': {
    padding: 0,
  },

  '& .MuiDataGrid-columnHeader[data-field="gapColumnLeft"]': {
    padding: 0,
  },

  '& .MuiDataGrid-columnHeader[data-field="gapColumnRight"]': {
    padding: 0,
  },

  '& .spacerRow': {
    backgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
  },
  '& .MuiDataGrid-sortButton': {
    backgroundColor: 'transparent !important',
  },
}));

export const StyledDataGridPC = styled(StyledDataGrid)(() => ({
  '& .customBackgroundHeader': {
    minHeight: '2.3125rem',
  },
}));

export const StyledIcon = styled(FontAwesomeIcon)({
  width: '1.125rem',
  height: '1.125rem',
  fontWeight: '900',
  lineHeight: '0.8125rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

export const StyledHeaderWrapper = styled(Box)(() => {
  return {
    display: 'flex',
    alignItems: 'flex-end',
    aligSelf: 'stretch',
    justifyContent: 'space-between',
    marginBottom: tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_com_padding_m,
    position: 'relative',
  };
});
export const StyledTitle = styled(Box)(() => {
  return {
    fontWeight: '700',
    color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
    fontFamily: tsoPqTheme.typography.openSans,
    lineHeight: '1.125rem',
    fontSize: '0.75rem',
  };
});
export const StyledActionsWrapper = styled(Box)(() => {
  return {
    display: 'flex',
    alignItems: 'center',
    aligSelf: 'stretch',
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_com_padding_m,
  };
});
export const StyledEditIconWrapper = styled(Box)(() => {
  return {
    marginTop: '0.5rem',
  };
});

export const customSortIconAsc = () => <FontAwesomeIcon icon={faSortUp} />;
export const customSortIconDesc = () => <FontAwesomeIcon icon={faSortDown} />;

export const dateTimeValueFormatter = (value: string | null | undefined): string => {
  if (!value || typeof value !== 'string') return '';

  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // we have to append this "Z", because otherwise JS will treat it as a local time and any "conversions to local" will fail.
    // Let's just append the "Z" so future developers will not spend any more time trying to understand why the conversion does not happen.
    const normalizedValue = value.endsWith('Z') || value.includes('+') ? value : `${value}Z`;

    const utcDate = parseISO(normalizedValue);
    const localDate = toZonedTime(utcDate, timeZone);

    return format(localDate, 'dd.MM.yyyy HH:mm');
  } catch (err) {
    console.error('Invalid date format:', value, err);
    return '';
  }
};

export const dateTimeSortComparator = (a: string | null, b: string | null) => {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  return parseISO(a).getTime() - parseISO(b).getTime();
};
